/**
 * Shell Application - Router Component
 * Project: Quantiix Shell (quantiix-shell)
 * Module: src/components/AppRouter.jsx
 */

import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout from './Layout';
import OperatorLayout from './OperatorLayout';
import LoginPage from '../auth/LoginPage';
import HomePage from './HomePage';
import SettingsPage from '../pages/Settings/SettingsPage';
import MicroFrontendLoader from '../micro-frontends/MicroFrontendLoader';
import ProtectedRoute from '../auth/ProtectedRoute';
import ShellBreadcrumbs from './ShellBreadcrumbs';
import { createLogger } from '../utils/logger';

const logger = createLogger('Shell::AppRouter');

const UnauthorizedPage = () => (
    <div className="unauthorized" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center'
    }}>
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
    </div>
);

const MicroFrontendContent = () => {
    const { appId, '*': subPath } = useParams();
    logger.info('Loading micro-frontend: ', { appId, subPath });
    return <MicroFrontendLoader appId={appId} subPath={subPath || ''} />;
};

const OperatorMFEContent = () => {
    const { machineId } = useParams();
    logger.info('Loading operator view: ', { machineId });
    return <MicroFrontendLoader appId="machine" subPath={machineId} mode="operator" />;
};

/**
 * Wrapper for authenticated routes with layout and breadcrumbs
 */
const AuthenticatedLayout = ({ children, requiredRole, requiredFeature, showBreadcrumbs = false }) => (
    <ProtectedRoute requiredRole={requiredRole} requiredFeature={requiredFeature}>
        <Layout>
            {showBreadcrumbs && <ShellBreadcrumbs />}
            {children}
        </Layout>
    </ProtectedRoute>
);

/**
 * Wrapper for operator routes - no navbar, no breadcrumbs
 */
const AuthenticatedOperatorLayout = ({ children }) => (
    <ProtectedRoute requiredRole="operator">
        <OperatorLayout>
            {children}
        </OperatorLayout>
    </ProtectedRoute>
);

export const AppRouter = () => {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Home - authentication only */}
            <Route path="/" element={
                <AuthenticatedLayout>
                    <HomePage />
                </AuthenticatedLayout>
            } />

            {/* Settings - shell-owned page */}
            <Route path="/app/settings" element={
                <AuthenticatedLayout>
                    <SettingsPage />
                </AuthenticatedLayout>
            } />

            {/* Operator route - MUST come BEFORE /app/:appId/* */}
            <Route path="/app/operator/:machineId" element={
                <AuthenticatedOperatorLayout>
                    <OperatorMFEContent />
                </AuthenticatedOperatorLayout>
            } />

            {/* Micro-frontend routes - with breadcrumbs */}
            <Route path="/app/:appId/*" element={
                <AuthenticatedLayout showBreadcrumbs={true}>
                    <MicroFrontendContent />
                </AuthenticatedLayout>
            } />

            {/* Admin route */}
            <Route path="/admin" element={
                <AuthenticatedLayout requiredRole="admin">
                    <div>Admin Panel</div>
                </AuthenticatedLayout>
            } />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};